<template>
  <app-confirm-dialog
    v-model="dialog"
    @confirm="confirm"
    @cancel="cancel"
    confirmBtnText="接受"
    cancelBtnText="拒絕"
    :loading="loading"
    :showConfirmBtn="showConfirmBtn"
    :showCancelBtn="showCancelBtn">
    <div class="subheading">{{dialogText}}</div>
    <v-progress-linear
      v-if="countdownProgress"
      v-model="countdownPercentage"
      color="secondary" />
  </app-confirm-dialog>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import {FudaoEvent, fudaoAppService} from 'langyan';

const getDefaultData = () => {
  return {
    loading: false,
    dialog: false,
    dialogText: '',
    showConfirmBtn: true,
    showCancelBtn: true,
    waitingTime: 0,
    countdownProgress: true,
    fudaoSession: {}
  };
};

export default {
  data: getDefaultData,
  computed: {
    ...mapState('fudao', {
      fudaoDto: state => state.fudaoDto,
      responseTimeout: state => state.responseTimeout
    }),

    isSelfInvite() {
      return this.fudaoDto.teacher.Id === this.userid;
    },

    countdownPercentage() {
      return Math.round(this.waitingTime/(this.responseTimeout/1000) * 100);
    }
  },

  watch: {
    dialog(value) {
      if (!value) {
        this.reset();
      }
    },

    waitingTime(value) {
      if (value < this.responseTimeout/1000) {
        return;
      }

      this.stopCountdownProgress();
      this.sentNoResponse();
    }
  },

  mounted() {
    this.$eventBus.$on(FudaoEvent.StudentStartSessionInvitationReceived, this.onStudentStartSessionInvitationReceived);
  },

  beforeDestroy() {
    this.$eventBus.$off(FudaoEvent.StudentStartSessionInvitationReceived, this.onStudentStartSessionInvitationReceived);
    this.reset();
  },

  methods: {
    ...mapActions('alert', {
      addAlert: 'add'
    }),

    isDifferentFudao(domainEvent) {
      return this.fudaoDto.id !== domainEvent.entityId;
    },

    isDifferentSession(domainEvent) {
      return this.fudaoSession.id && this.fudaoSession.id !== domainEvent.body.id;
    },

    stopCountdownProgress() {
      clearInterval(this._countdownTimeInterval);
      this.countdownProgress = false;
    },

    open() {
      this.dialog = true;
    },

    reset() {
      this.$eventBus.$off(FudaoEvent.TeacherStartSessionInvitationCanceled, this.onTeacherStartSessionInvitationCanceled);
      this.$eventBus.$off(FudaoEvent.StudentStartFudaoSessionReceived, this.onStudentStartFudaoSessionReceived);
      this.$eventBus.$off(FudaoEvent.StudentStartSessionInvitationNoReponse, this.onStudentStartSessionInvitationNoReponse);
      this.$eventBus.$off(FudaoEvent.FudaoSessionStarted, this.onFudaoSessionStarted);
      this.$eventBus.$off(FudaoEvent.FudaoSessionEnded, this.onFudaoSessionEnded);
      this.$eventBus.$off(FudaoEvent.FudaoSessionFailed, this.onFudaoSessionFailed);

      this.stopCountdownProgress();
      let data = getDefaultData();
      Object.keys(data).forEach(key => this[key] = data[key]);
    },

    async confirm() {
      if (!this.isStudent || this.fudaoDto.teacher.Id === this.userid) {
        this.addAlert({level: 'error', content: '無法接受邀請: 角色錯誤'});
        this.dialog = false;
        return;
      }

      this.stopCountdownProgress();
      this.loading = true;
      this.showCancelBtn = false;
      this.dialogText = '等候對方回應...';
      await fudaoAppService.acceptFudaoStartSessionInvitionAsync(this.fudaoDto.id, this.fudaoSession);
    },

    async cancel() {
      this.stopCountdownProgress();
      await fudaoAppService.rejectFudaoStartSessionInvitionAsync(this.fudaoDto.id, this.fudaoSession);
      this.dialog = false;
    },

    sentNoResponse() {
      fudaoAppService.noResponseFudaoStartSessionInvitionAsync(this.fudaoDto.id, this.fudaoSession);
    },

    // events ---------------------------------------------------

    onTeacherStartSessionInvitationCanceled(domainEvent) {
      if (this.isSelfInvite || this.isDifferentFudao(domainEvent)) {
        return;
      }
      this.addAlert({level: 'info', content: '老師取消邀請'});
      this.dialog = false;
    },

    onStudentStartSessionInvitationReceived(domainEvent) {
      if (this.isSelfInvite || this.isDifferentFudao(domainEvent)) {
        return;
      }

      this.$eventBus.$on(FudaoEvent.TeacherStartSessionInvitationCanceled, this.onTeacherStartSessionInvitationCanceled);
      this.$eventBus.$on(FudaoEvent.StudentStartFudaoSessionReceived, this.onStudentStartFudaoSessionReceived);
      this.$eventBus.$on(FudaoEvent.StudentStartSessionInvitationNoReponse, this.onStudentStartSessionInvitationNoReponse);
      this.$eventBus.$on(FudaoEvent.FudaoSessionStarted, this.onFudaoSessionStarted);
      // session沒建起來也有可能發生FudaoSessionEnded事件，因此要接handler將dialog關閉
      this.$eventBus.$on(FudaoEvent.FudaoSessionEnded, this.onFudaoSessionEnded);
      this.$eventBus.$on(FudaoEvent.FudaoSessionFailed, this.onFudaoSessionFailed);

      this.fudaoSession = domainEvent.body;
      let sessionType = domainEvent.body.SessionType;
      switch (sessionType) {
        case 'TeacherVideo':
          this.dialogText = '老師向你發送視訊分享邀請，是否同意使用視訊?';
          break;

        case 'Audio':
          this.dialogText = '老師向你發送音訊分享邀請，是否同意使用通話?';
          break;

        case 'Whiteboard':
          this.dialogText = '老師向你發送白板分享邀請，是否同意使用白板?';
          break;
      }

      this.dialog = true;

      this._countdownTimeInterval = setInterval(() => {
        this.waitingTime++;
      }, 1000);
    },

    onStudentStartFudaoSessionReceived(domainEvent) {
      if (this.isSelfInvite || this.isDifferentFudao(domainEvent) || this.isDifferentSession(domainEvent)) {
        return;
      }

      let containerElm = document.getElementById('fudaoVideo');
      switch (this.fudaoSession.SessionType) {
        case 'TeacherVideo':
          fudaoAppService.startFudaoSessionAsync(this.fudaoDto.id, this.fudaoSession, containerElm);
          break;

        case 'Audio':
          fudaoAppService.startFudaoSessionAsync(this.fudaoDto.id, this.fudaoSession);
          break;

        case 'Whiteboard':
          fudaoAppService.startFudaoSessionAsync(this.fudaoDto.id, this.fudaoSession, containerElm);
          break;
      }
    },

    onStudentStartSessionInvitationNoReponse(domainEvent) {
      if (this.isSelfInvite || this.isDifferentFudao(domainEvent) || this.isDifferentSession(domainEvent)) {
        return;
      }
      this.addAlert({level: 'warning', content: '回應逾時'});
      this.dialog = false;
    },

    onFudaoSessionStarted(domainEvent) {
      let msg;
      let sessionType = domainEvent.body.SessionType;
      switch (sessionType) {
        case 'TeacherVideo':
          msg = '視訊分享已開始';
          break;

        case 'Audio':
          msg = '音訊分享已開始';
          break;

        case 'Whiteboard':
          msg = '白板分享已開始';
          break;
      }
      this.addAlert({level: 'info', content: msg});
      this.dialog = false;
    },

    onFudaoSessionEnded() {
      this.dialog = false;
    },

    onFudaoSessionFailed(errMsg) {
      this.addAlert({level: 'error', content: '邀請分享發生錯誤'});
      this.dialog = false;
    }
  }
};
</script>

<style lang="stylus" scoped>
</style>
